import { utcIsoToZonedParts } from '@eureca/ui';
import { APP_TIMEZONE } from '@/lib/timezone';
import type { ProgramStatus } from './ProgramsPage.component';

/**
 * Civil-day index (days since epoch) of a UTC instant, read in
 * America/Sao_Paulo — so the day count matches the BRT calendar the consultor
 * sees, not the UTC one (an evening-BRT deadline is the next UTC day).
 */
function brtDayIndex(iso: string): number | null {
  const { date } = utcIsoToZonedParts(iso, APP_TIMEZONE);
  if (!date) return null;
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return null;
  return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
}

/**
 * EU-405 — dias restantes até o fim do prazo (`endApplying`), exibido no badge
 * "N dias" da coluna Início. Substitui o antigo `daysOnAir` (EU-238), que
 * contava a partir de `startDate`/`publishedAt` e exibia "0 dias" para todos.
 *
 * Contagem INCLUSIVA por dia de calendário BRT (conta hoje + o dia do
 * encerramento) — alinhada à convenção de countdown do app candidato
 * (`formatDeadlineCountdownLong`): fim 30/06 visto em 24/06 → "7 dias",
 * fim 25/06 visto em 24/06 → "2 dias". Recalcula a cada render a partir do
 * relógio do cliente (`now`), então o valor se atualiza sozinho a cada dia
 * sem refetch.
 *
 * Só os status ativos (Ativa / Ativo e privado) têm prazo no ar; os demais
 * (rascunho/encerrado/cancelado) retornam `null` → sem badge. Prazo já vencido
 * é fixado em `0`.
 */
export function computeProgramDaysRemaining(
  endApplyingIso: string | null,
  status: ProgramStatus,
  now: Date
): number | null {
  if (status !== 'ativa' && status !== 'ativo-privado') return null;
  if (!endApplyingIso) return null;
  const endIdx = brtDayIndex(endApplyingIso);
  const todayIdx = brtDayIndex(now.toISOString());
  if (endIdx === null || todayIdx === null) return null;
  const remaining = endIdx - todayIdx + 1; // inclusivo (hoje + dia do fim)
  return remaining > 0 ? remaining : 0;
}

/**
 * EU-404 — célula "Inscritos". O total exato (inclusive `0`) aparece para
 * programas onde inscrição faz sentido; `—` fica reservado a `rascunho` (ainda
 * sem publicação, dado inaplicável). Antes, `0` virava `—` em qualquer status,
 * confundindo "zero inscrições" com "dado não carregado".
 */
export function formatProgramRegistrations(status: ProgramStatus, count: number): string {
  if (status === 'rascunho') return '—';
  return count.toLocaleString('pt-BR');
}
